import { useEffect, useState } from 'react';
import { useDiagramWithTanstackDBLink } from '../../hooks/useDiagramWithTanstackDBLink';
import { ShapeMapAdapter } from '../../lib/diagram/ShapeMapAdapter';
import { ShapePersistenceGateway } from '../../lib/diagram/shapeGeometry';

export default function TestPersistencePage() {
  const diagramId = 'test-diagram-1';
  const { ydoc, isReady } = useDiagramWithTanstackDBLink({ 
    diagramId, 
    userId: 'test-user',
    userName: 'Test User',
    isLinkMode: false // Use authenticated mode for test
  });
  const isConnected = true; // Always connected with new architecture
  const [status, setStatus] = useState('Initializing...');
  const [shapeCount, setShapeCount] = useState(0);
  const [lastUpdate, setLastUpdate] = useState<string>('');

  useEffect(() => {
    if (!ydoc || !isReady) return;

    const shapesMap = ydoc.getMap<any>('shapes');
    
    // Update shape count
    const updateCount = () => {
      setShapeCount(shapesMap.size);
      setLastUpdate(new Date().toISOString());
    };

    // Initial count
    updateCount();

    // Listen for changes
    const observer = () => updateCount();
    shapesMap.observe(observer);

    // Connection status
    if (isConnected) {
      setStatus('Connected via WebSocket');
    } else {
      setStatus('Not connected (offline mode)');
    }

    return () => {
      shapesMap.unobserve(observer);
    };
  }, [ydoc, isReady, isConnected]);

  const addTestShape = () => {
    if (!ydoc) return;
    const shapeId = `shape-${Date.now()}`;
    
    const newShape = {
      id: shapeId,
      type: 'person',
      x: Math.random() * 500,
      y: Math.random() * 500,
      width: 100,
      height: 100,
      props: {
        name: `Test Shape ${shapeCount + 1}`,
        sex: 'male'
      }
    };

    console.log('➕ Adding shape:', newShape);
    new ShapePersistenceGateway(new ShapeMapAdapter(ydoc.getMap('shapes'))).create(newShape);
    setStatus(`Added shape: ${shapeId}`);
  };

  const clearAllShapes = () => {
    if (!ydoc) return;
    new ShapePersistenceGateway(new ShapeMapAdapter(ydoc.getMap('shapes'))).clear();
    setStatus('Cleared all shapes');
  };

  const forceSync = async () => {
    setStatus('Snapshot auto-saves every 2 minutes or 10 updates');
    // In Electric v2, snapshots are client-driven and automatic
  };

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Persistence Test for {diagramId}</h1>
      
      <div className="bg-gray-100 p-4 rounded mb-6 space-y-2">
        <div className="font-mono text-sm">
          <div>Status: {status}</div>
          <div>Shape Count: {shapeCount}</div>
          <div>Last Update: {lastUpdate}</div>
          <div>WebSocket: {isConnected ? 'Connected' : 'Disconnected'}</div>
        </div>
      </div>

      <div className="space-x-4 mb-6">
        <button
          onClick={addTestShape}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Add Test Shape
        </button>
        <button
          onClick={clearAllShapes}
          className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
        >
          Clear All Shapes
        </button>
        <button
          onClick={forceSync}
          className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
        >
          Force Sync
        </button>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
        >
          Refresh Page
        </button>
      </div>

      <div className="bg-yellow-100 p-4 rounded">
        <h2 className="font-bold mb-2">Test Instructions:</h2>
        <ol className="list-decimal list-inside space-y-1">
          <li>Click "Add Test Shape" to add shapes</li>
          <li>Watch the shape count increase</li>
          <li>Click "Refresh Page" to reload</li>
          <li>After refresh, the shape count should remain the same (persisted)</li>
          <li>If shapes disappear after refresh, persistence is not working</li>
        </ol>
      </div>
    </div>
  );
}
